import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getPreferences, updateSettings } from '../api/preferences'
import type { NotificationConfig, NotificationSettings, Preferences } from '../types'

// Single cache key — all three slices (settings, channels, phones) come from one GET /api/v1/notifications call.
export const PREFERENCES_KEY = ['notifications', 'preferences'] as const

// Aliases kept for backward compatibility with existing imports in Preferences.tsx
export const SETTINGS_KEY = PREFERENCES_KEY
export const CHANNELS_KEY = PREFERENCES_KEY

// Preferences are actively edited — always fetch fresh (staleTime: 0 overrides the global 30s default).
// keepPreviousData is intentionally NOT used here: showing stale channels/phones while refetching
// can cause the UI to report "0 channels" while the backend still has one, masking real errors.
export function usePreferences() {
  return useQuery<Preferences>({
    queryKey: PREFERENCES_KEY,
    queryFn: getPreferences,
    staleTime: 0,
  })
}

export function useNotificationSettings() {
  return useQuery<Preferences, Error, NotificationSettings>({
    queryKey: PREFERENCES_KEY,
    queryFn: getPreferences,
    select: (data) => data.settings,
    staleTime: 0,
  })
}

export function useChannels() {
  return useQuery<Preferences, Error, NotificationConfig[]>({
    queryKey: PREFERENCES_KEY,
    queryFn: getPreferences,
    select: (data) => data.channels,
    staleTime: 0,
  })
}

export function useUpdateSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (settings: Partial<NotificationSettings>) => updateSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY })
    },
  })
}
